import Intercom from '@intercom/messenger-js-sdk';

class IntercomService {
  constructor() {
    this.appId = process.env.REACT_APP_AP_INTERCOM_APP_ID;
    this.apiBase = process.env.REACT_APP_AP_INTERCOM_API_BASE || 'https://api-iam.intercom.io';
    this.isInitialized = false;
  }

  // Initialize Intercom with basic settings
  initialize() {
    if (!this.appId) {
      return;
    }

    // Initialize Intercom
    Intercom({
      app_id: this.appId,
      api_base: this.apiBase,
    });
    this.isInitialized = true;
  }

  // Boot Intercom with user data
  boot(userData) {
    if (!this.isInitialized) {
      this.initialize();
    }

    if (!userData) {
      return;
    }

    // Prepare user data with proper formatting
    const intercomUserData = {
      app_id: this.appId,
      user_id: userData.id,
      name: userData.name || userData.firstName,
      email: userData.email,
    };

    // Add company data if available
    if (userData.organizationId || userData.companyId || userData.orgId) {
      intercomUserData.company = userData.organizationId || userData.companyId || userData.orgId;
    }

    // Boot Intercom with user data
    window.Intercom('boot', intercomUserData);
  }

  // Update Intercom with new user data
  update(userData) {
    if (!this.isInitialized) {
      return;
    }

    if (!userData) {
      return;
    }

    // For updates, we can either call update with new data or just call update()
    window.Intercom('update');
  }

  // Shutdown Intercom (for logout)
  shutdown() {
    if (this.isInitialized) {
      window.Intercom('shutdown');
      this.isInitialized = false;
    }
  }

  // Convert ISO date string to Unix timestamp
  convertToUnixTimestamp(dateString) {
    if (!dateString) return undefined;

    // Handle different date formats
    const date = new Date(dateString);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    return Math.floor(date.getTime() / 1000);
  }

  // Check if Intercom is available
  isAvailable() {
    return typeof Intercom !== 'undefined' && this.isInitialized;
  }

  // Check if user should have Intercom enabled based on features and organization
  shouldEnableIntercom(userData) {
    if (!userData) return false;

    const { features, orgId, organizationId, companyId } = userData;

    // Check if user has SUPPORT feature or is part of an organization
    return !!(features && features.includes('SUPPORT')) || !!orgId || !!organizationId || !!companyId;
  }
}

// Export singleton instance
export default new IntercomService();

// Also export the class for testing
export { IntercomService };
